import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing focus restoration in popup components.
 * Captures the currently focused element when popup opens and restores it when popup closes.
 * Only activates for time-based popups to avoid interfering with intentional user actions.
 *
 * @param isOpen - Boolean indicating if the popup is open
 */
export const useFocusRestoration = (isOpen: boolean) => {
  const previousActiveElementRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Capture the currently focused element before popup takes focus
      previousActiveElementRef.current = document.activeElement;
    } else if (previousActiveElementRef.current) {
      // Restore focus when popup closes
      // Use requestAnimationFrame to ensure the popup is fully removed from DOM
      requestAnimationFrame(() => {
        const elementToFocus = previousActiveElementRef.current as HTMLElement;

        // Check if element still exists and is focusable
        if (elementToFocus && document.contains(elementToFocus)) {
          try {
            // Simple focus restoration for all elements
            elementToFocus.focus();
          } catch {
            // Fallback: clear the stored reference
            previousActiveElementRef.current = null;
          }
        } else {
          previousActiveElementRef.current = null;
        }
      });
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      // Clear the reference on unmount to prevent memory leaks
      previousActiveElementRef.current = null;
    };
  }, []);
};
